import { NavLink, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";

type NavIconName =
  | "dashboard"
  | "priorities"
  | "leads"
  | "directory"
  | "saas"
  | "manual"
  | "submissions"
  | "invitations"
  | "reports"
  | "billing"
  | "pages"
  | "services"
  | "plans"
  | "portfolio"
  | "cases"
  | "blog"
  | "categories"
  | "faqs"
  | "media"
  | "settings";

type NavItem = { to: string; label: string; icon: NavIconName };
type NavSection = { label: string; items: NavItem[] };

const navSections: NavSection[] = [
  {
    label: "GENERAL",
    items: [{ to: "/", label: "Dashboard", icon: "dashboard" }],
  },
  {
    label: "OPERACIONES",
    items: [
      { to: "/prioridades", label: "Prioridades", icon: "priorities" },
      { to: "/leads", label: "Leads", icon: "leads" },
      { to: "/solicitudes", label: "Solicitudes", icon: "submissions" },
      { to: "/invitaciones", label: "Invitaciones", icon: "invitations" },
      { to: "/cobros", label: "Cobros", icon: "billing" },
    ],
  },
  {
    label: "DIRECTORIO",
    items: [
      { to: "/directorio", label: "Vista general", icon: "directory" },
      { to: "/directorio/saas", label: "SaaS", icon: "saas" },
      { to: "/directorio/manuales", label: "Manuales", icon: "manual" },
      { to: "/reportes-fichas", label: "Reportes de fichas", icon: "reports" },
    ],
  },
  {
    label: "CONTENIDO",
    items: [
      { to: "/paginas", label: "Páginas", icon: "pages" },
      { to: "/servicios", label: "Servicios", icon: "services" },
      { to: "/planes", label: "Planes", icon: "plans" },
      { to: "/portafolio", label: "Portafolio", icon: "portfolio" },
      { to: "/casos", label: "Casos", icon: "cases" },
      { to: "/blog", label: "Blog", icon: "blog" },
      { to: "/categorias", label: "Categorías", icon: "categories" },
      { to: "/faqs", label: "FAQs", icon: "faqs" },
      { to: "/media", label: "Media", icon: "media" },
    ],
  },
  {
    label: "SISTEMA",
    items: [{ to: "/configuracion", label: "Configuración", icon: "settings" }],
  },
];

function useOpenDirectoryReportCount() {
  return useQuery({
    queryKey: ["directory_profile_reports_count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("directory_profile_reports")
        .select("id", { count: "exact", head: true })
        .eq("status", "open");
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

function NavIcon({ name, className = "" }: { name: NavIconName; className?: string }) {
  const common = {
    className,
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true as const,
  };

  switch (name) {
    case "dashboard":
      return (
        <svg {...common}>
          <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
          <rect x="13.5" y="3.5" width="7" height="5" rx="1.5" />
          <rect x="13.5" y="11.5" width="7" height="9" rx="1.5" />
          <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
        </svg>
      );
    case "leads":
      return (
        <svg {...common}>
          <path d="M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20" />
          <circle cx="10" cy="7.5" r="3.5" />
          <path d="M16 4.4a3.5 3.5 0 0 1 0 6.8M20 20v-1.5a3.5 3.5 0 0 0-2.5-3.35" />
        </svg>
      );
    case "priorities":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 7v5l3.5 2M19 5l1.5-1.5M5 5 3.5 3.5" />
        </svg>
      );
    case "directory":
      return (
        <svg {...common}>
          <path d="M3.5 10.5h17l-1.4-6h-14zM5 10.5V20h14v-9.5M9 20v-5h6v5" />
          <path d="M3.5 10.5a2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0" />
        </svg>
      );
    case "billing":
      return (
        <svg {...common}>
          <rect x="4" y="3" width="16" height="18" rx="2" />
          <path d="M8 7h8M8 11h8M8 15h3" />
          <path d="M15.5 15.5c.4-.5 1-.8 1.7-.8 1 0 1.8.5 1.8 1.3 0 1.7-3.4 1.1-3.4 2.8 0 .8.8 1.3 1.8 1.3.7 0 1.3-.3 1.7-.8M17.4 14v1" />
        </svg>
      );
    case "saas":
      return (
        <svg {...common}>
          <path d="M7 18.5h10.2a3.3 3.3 0 0 0 .4-6.6A5.8 5.8 0 0 0 6.5 10a4.3 4.3 0 0 0 .5 8.5Z" />
          <path d="M8.5 14.5h7M10 17h4" />
        </svg>
      );
    case "manual":
      return (
        <svg {...common}>
          <path d="M4 7.5h16v12H4zM8 7.5V5h8v2.5M4 12h16M10 12v2h4v-2" />
        </svg>
      );
    case "submissions":
      return (
        <svg {...common}>
          <path d="M8 4h8l3 3v13H5V4h3Z" />
          <path d="M8.5 11h7M8.5 15h7M14 4v4h4" />
        </svg>
      );
    case "reports":
      return (
        <svg {...common}>
          <path d="M12 3 3.5 7v5c0 4.7 3.2 7.5 8.5 9 5.3-1.5 8.5-4.3 8.5-9V7L12 3Z" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      );
    case "invitations":
      return (
        <svg {...common}>
          <path d="M10 13.5 8.5 15a3.2 3.2 0 0 1-4.5-4.5l4-4a3.2 3.2 0 0 1 4.5 0" />
          <path d="m14 10.5 1.5-1.5a3.2 3.2 0 0 1 4.5 4.5l-4 4a3.2 3.2 0 0 1-4.5 0" />
          <path d="m9 15 6-6" />
        </svg>
      );
    case "pages":
      return (
        <svg {...common}>
          <path d="M7 3.5h7l4.5 4.5v12.5H7a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z" />
          <path d="M14 3.5V8h4.5M8.5 12h7M8.5 16h7" />
        </svg>
      );
    case "services":
      return (
        <svg {...common}>
          <path d="m13.5 2.5-9 11h7l-1 8 9-12h-7z" />
        </svg>
      );
    case "plans":
      return (
        <svg {...common}>
          <path d="m12 3 9 5-9 5-9-5 9-5Z" />
          <path d="m3 12 9 5 9-5M3 16l9 5 9-5" />
        </svg>
      );
    case "portfolio":
      return (
        <svg {...common}>
          <rect x="3.5" y="7" width="17" height="13" rx="2" />
          <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3.5 12h17M10 12v2h4v-2" />
        </svg>
      );
    case "cases":
      return (
        <svg {...common}>
          <path d="M4 20V9.5L12 4l8 5.5V20H4Z" />
          <path d="M9 20v-6h6v6M8 10h.01M12 10h.01M16 10h.01" />
        </svg>
      );
    case "blog":
      return (
        <svg {...common}>
          <path d="M5 4h14a2 2 0 0 1 2 2v14H7a3 3 0 0 1-3-3V6a2 2 0 0 1 1-2Z" />
          <path d="M8 8h8M8 12h8M8 16h5M4 17a3 3 0 0 1 3-3h14" />
        </svg>
      );
    case "categories":
      return (
        <svg {...common}>
          <path d="M20 13 11 22l-9-9V3h10l8 8a1.4 1.4 0 0 1 0 2Z" />
          <circle cx="7" cy="7" r="1.25" />
        </svg>
      );
    case "faqs":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.5 9a2.6 2.6 0 1 1 4.3 2c-1.1.9-1.8 1.2-1.8 2.7M12 17.3h.01" />
        </svg>
      );
    case "media":
      return (
        <svg {...common}>
          <rect x="3.5" y="4" width="17" height="16" rx="2" />
          <circle cx="9" cy="9" r="1.5" />
          <path d="m4 17 5-5 3.5 3 2.5-2 5 4" />
        </svg>
      );
    case "settings":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path
            d="m19.4 15 .1.1 1.3 1-1.3 2.3-1.6-.6a8 8 0 0 1-1.7 1l-.3 1.7h-2.7l-.3-1.7a8 8 0 0 1-1.7-1l-1.6.6-1.3-2.3 1.3-1a7 7 0 0 1 0-2l-1.3-1 1.3-2.3 1.6.6a8 8 0 0 1 1.7-1l.3-1.7h2.7l.3 1.7a8 8 0 0 1 1.7 1l1.6-.6 1.3 2.3-1.3 1a7 7 0 0 1 0 2Z"
            transform="translate(-1 -1) scale(1.08)"
          />
        </svg>
      );
  }
}

function Brand({ light = false }: { light?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid h-10 w-10 place-items-center rounded-xl bg-blue-500 text-sm font-black tracking-tight text-white shadow-lg shadow-blue-950/30">
        VD
      </span>
      <div className="leading-tight">
        <p className={`text-sm font-bold tracking-[0.12em] ${light ? "text-slate-950" : "text-white"}`}>
          VIMOS
        </p>
        <p
          className={`mt-1 text-[10px] font-semibold tracking-[0.2em] ${light ? "text-slate-500" : "text-slate-400"}`}
        >
          DIGITAL · ADMIN
        </p>
      </div>
    </div>
  );
}

function getInitials(name?: string | null) {
  if (!name?.trim()) return "VD";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function roleLabel(role?: string) {
  if (role === "admin") return "Administrador";
  if (role === "editor") return "Editor";
  return "Lector";
}

function MobileNavigation() {
  const reportCountQuery = useOpenDirectoryReportCount();
  const openReportCount = reportCountQuery.data ?? 0;
  return (
    <nav className="mt-3 flex gap-1.5 overflow-x-auto pb-1" aria-label="Navegación móvil del admin">
      {navSections
        .flatMap((section) => section.items)
        .map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/" || item.to === "/directorio"}
            className={({ isActive }) =>
              `inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${
                isActive
                  ? "border-blue-200 bg-blue-50 text-blue-800"
                  : "border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-50"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <NavIcon name={item.icon} className={isActive ? "text-blue-700" : "text-slate-400"} />
                {item.label}
                {item.to === "/reportes-fichas" && openReportCount > 0 ? (
                  <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-700">
                    {openReportCount > 99 ? "99+" : openReportCount}
                  </span>
                ) : null}
              </>
            )}
          </NavLink>
        ))}
    </nav>
  );
}

export function AdminShell() {
  const { profile } = useAuth();
  const reportCountQuery = useOpenDirectoryReportCount();
  const openReportCount = reportCountQuery.data ?? 0;

  return (
    <div className="min-h-screen bg-[#f7f8fa] lg:flex lg:h-screen lg:min-h-0 lg:overflow-hidden">
      <aside className="hidden w-[264px] flex-none flex-col border-r border-slate-800 bg-slate-950 px-3 py-5 lg:sticky lg:top-0 lg:flex lg:h-screen lg:self-start">
        <div className="border-b border-white/[0.08] px-3 pb-5">
          <Brand light />
        </div>

        <nav
          className="mt-5 min-h-0 flex-1 space-y-6 overflow-y-auto px-1 pb-4"
          aria-label="Navegación administrativa"
        >
          {navSections.map((section) => (
            <div key={section.label}>
              <p className="mb-2 px-3 text-[10px] font-bold tracking-[0.16em] text-slate-500">
                {section.label}
              </p>
              <div className="space-y-1">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/" || item.to === "/directorio"}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-medium transition-colors ${
                        isActive
                          ? "bg-blue-500/15 text-white shadow-sm shadow-black/10 before:absolute before:bottom-2 before:left-0 before:top-2 before:w-[3px] before:rounded-r-full before:bg-blue-400"
                          : "text-slate-400 hover:bg-white/[0.06] hover:text-slate-100"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <NavIcon
                          name={item.icon}
                          className={
                            isActive
                              ? "text-blue-300"
                              : "text-slate-500 transition-colors group-hover:text-slate-300"
                          }
                        />
                        <span>{item.label}</span>
                        {item.to === "/reportes-fichas" && openReportCount > 0 ? (
                          <span
                            aria-label={`${openReportCount} reportes pendientes`}
                            className="ml-auto rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-bold text-rose-200"
                          >
                            {openReportCount > 99 ? "99+" : openReportCount}
                          </span>
                        ) : null}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-white/[0.08] pt-4">
          <div className="flex items-center gap-3 rounded-xl px-2 py-2">
            <span className="grid h-9 w-9 flex-none place-items-center rounded-full border border-white/10 bg-slate-800 text-xs font-bold text-slate-200">
              {getInitials(profile?.full_name)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-slate-200">
                {profile?.full_name ?? "Equipo Vimos"}
              </p>
              <p className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-slate-500">
                {roleLabel(profile?.role)}
              </p>
            </div>
            <button
              onClick={() => supabase.auth.signOut()}
              className="rounded-lg p-2 text-slate-500 transition hover:bg-white/[0.08] hover:text-white"
              aria-label="Cerrar sesión"
              title="Cerrar sesión"
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M10 17l5-5-5-5M15 12H3" />
                <path d="M12 3h6a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-6" />
              </svg>
            </button>
          </div>
        </div>
      </aside>

      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 px-4 py-3 shadow-sm shadow-slate-900/[0.03] backdrop-blur lg:hidden">
        <div className="flex items-center justify-between gap-3">
          <Brand />
          <button
            onClick={() => supabase.auth.signOut()}
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
          >
            Salir
          </button>
        </div>
        <MobileNavigation />
      </header>

      <main className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <Outlet />
      </main>
    </div>
  );
}
